/* ============================================================================
   Dashboard widgets — API-driven availability/enablement

   The widget APIs return only enum names; the UI maps each enum to a dashboard
   card (whose title/icon/size live in the card catalogs). Two sources:
     • GET /dashboard-widgets        → the available catalog (all widgets)
     • GET /dashboard/grc/my-widgets → the user's enabled widgets (per role)

   Group level can't assign roles, so it shows the whole catalog; the per-org
   dashboard shows the union of enabled widgets across the user's roles.
   ========================================================================== */

import { getAPI, putAPI, deleteAPI } from "../../../api/frameworksApi";
import { API_URL } from "../../../constants/index";

export type WidgetKey =
  | "ASSESSMENT_STATUS_OVERVIEW"
  | "ASSESSMENTS_RUNNING"
  | "OPEN_FINDINGS"
  | "COMPLIANCE_SCORE"
  | "ACTIVE_SSPS"
  | "TOTAL_APPLICATIONS"
  | "TOP_RISK_APPLICATIONS"
  | "TOP_RISK_CONTRIBUTORS"
  | "ORGANIZATION_PERFORMANCE_OVERVIEW";

/* widget enum → per-org dashboard card id */
export const ORG_WIDGET_TO_CARD: Partial<Record<WidgetKey, string>> = {
  TOTAL_APPLICATIONS:         "total_apps",
  ACTIVE_SSPS:                "active_ssps",
  COMPLIANCE_SCORE:           "compliance_score",
  OPEN_FINDINGS:              "open_findings",
  ASSESSMENTS_RUNNING:        "assessments_running",
  ASSESSMENT_STATUS_OVERVIEW: "assessment_pipeline",
  TOP_RISK_APPLICATIONS:      "top_risk_apps",
};

/* widget enum → group dashboard card id */
export const GROUP_WIDGET_TO_CARD: Partial<Record<WidgetKey, string>> = {
  TOTAL_APPLICATIONS:               "total_orgs",
  ACTIVE_SSPS:                      "active_ssps",
  COMPLIANCE_SCORE:                 "group_compliance",
  TOP_RISK_APPLICATIONS:            "high_risk_orgs",
  OPEN_FINDINGS:                    "open_findings",
  ASSESSMENTS_RUNNING:              "active_assessments",
  ASSESSMENT_STATUS_OVERVIEW:       "assessment_status",
  TOP_RISK_CONTRIBUTORS:            "top_risk_contributors",
  ORGANIZATION_PERFORMANCE_OVERVIEW: "org_performance",
};

/* Cards kept regardless of the catalog (no widget enum / mock-backed). */
export const ORG_EXTRA_CARDS = ["compliance_framework" /* , "poam_status" — Findings & POA&M commented out */];
export const GROUP_EXTRA_CARDS: string[] = [];

/* Map a list/set of widget enums to card ids (in the given order). */
export function widgetsToCards(
  widgets: WidgetKey[],
  map: Partial<Record<WidgetKey, string>>,
): string[] {
  return widgets.map((w) => map[w]).filter((id): id is string => !!id);
}

/* ── Enabled-driven gating (per role) ──────────────────────────────────────
   The user's enabled widgets are authoritative for what a dashboard shows.
   Map them to this dashboard's card ids (catalog order) and report whether
   nothing is enabled, so the dashboard can render a "configure widgets"
   message in the centre instead of any cards. */
export function enabledCardIds(
  enabled: WidgetKey[],
  map: Partial<Record<WidgetKey, string>>,
  isValidCard: (id: string) => boolean,
): { ids: string[]; allDisabled: boolean } {
  const ids = widgetsToCards(enabled, map).filter(isValidCard);
  return { ids, allDisabled: ids.length === 0 };
}

/* GET /dashboard-widgets → the full available catalog. */
export async function fetchWidgetCatalog(): Promise<WidgetKey[]> {
  const res = await getAPI(API_URL.GRC_WIDGET_CATALOG);
  return (res?.data ?? []) as WidgetKey[];
}

export interface MyWidgetEntry {
  widget: WidgetKey;
  enabled: boolean;
}

export interface MyWidgetRole {
  roleName: string;
  orgUnitId: string;
  widgets: MyWidgetEntry[];
}

export interface MyWidgetsResponse {
  userId: string;
  roles: MyWidgetRole[];
}

/* GET /dashboard/grc/my-widgets → full structured response of widgets per role. */
export async function fetchMyWidgets(): Promise<MyWidgetsResponse> {
  const res = await getAPI(API_URL.GRC_MY_WIDGETS);
  return res?.data ?? { userId: "", roles: [] };
}

/* PUT/DELETE /dashboard/grc/my-widgets/{widget}?org_id={orgUnitId} → toggle user dashboard widget preference. */
export async function toggleMyWidget(
  widget: WidgetKey,
  orgUnitId: string,
  enable: boolean,
): Promise<MyWidgetsResponse> {
  const url = `${API_URL.GRC_MY_WIDGETS}/${widget}?org_id=${orgUnitId}`;
  const res = enable ? await putAPI(url, {}) : await deleteAPI(url);
  return res?.data ?? { userId: "", roles: [] };
}

/* GET /dashboard/grc/my-widgets → union of enabled widgets across roles. */
export async function fetchMyEnabledWidgets(): Promise<WidgetKey[]> {
  const res = await fetchMyWidgets();
  const roles = res.roles ?? [];
  const enabled = new Set<WidgetKey>();
  for (const role of roles) {
    for (const w of role.widgets ?? []) {
      if (w.enabled) enabled.add(w.widget);
    }
  }
  return Array.from(enabled);
}

/* ── Config (admin): role widgets per org ──────────────────────────────── */
const roleWidgetsUrl = (orgId: string, roleId: string) =>
  `${API_URL.GRC_ROLE_WIDGETS}/${orgId}/roles/${roleId}`;

/* A role's widget as returned by the role-widgets endpoint. `recommendation`
   is optional metadata (e.g. "PRIMARY_RECOMMENDATION") the backend tags certain
   widgets with for some roles.

   The list is the EFFECTIVE set for the org — explicit grants plus those
   inherited from an ancestor (group/org). `source` says which, and
   `inheritedFrom` names the ancestor it cascaded from when INHERITED. */
export interface RoleWidget {
  widget: WidgetKey;
  recommendation?: string;
  source?: "EXPLICIT" | "INHERITED";
  inheritedFrom?: string | null;
}

/* GET → the role's widgets in an org, with their optional recommendation tag. */
export async function fetchRoleWidgets(
  orgId: string,
  roleId: string,
): Promise<RoleWidget[]> {
  const res = await getAPI(roleWidgetsUrl(orgId, roleId));
  return (res?.data?.widgets ?? []) as RoleWidget[];
}

/* GET → the role's currently-enabled widgets in an org (only enabled appear). */
export async function fetchRoleEnabledWidgets(
  orgId: string,
  roleId: string,
): Promise<WidgetKey[]> {
  const widgets = await fetchRoleWidgets(orgId, roleId);
  return widgets.map((w) => w.widget);
}

/* PUT → enable a widget for the role in the org. */
export async function enableRoleWidget(orgId: string, roleId: string, widget: WidgetKey) {
  await putAPI(`${roleWidgetsUrl(orgId, roleId)}/widgets/${widget}`, {});
}

/* DELETE → disable a widget for the role in the org. */
export async function disableRoleWidget(orgId: string, roleId: string, widget: WidgetKey) {
  await deleteAPI(`${roleWidgetsUrl(orgId, roleId)}/widgets/${widget}`);
}

/* One pin in a bulk-save body. enabled=true → pin ON, enabled=false → pin OFF
   (overrides an inherited ON). Widgets left OUT of the body inherit from the
   parent — so the Save button only sends the ones the admin explicitly set. */
export interface WidgetPin {
  widget: WidgetKey;
  enabled: boolean;
}

/* PUT .../orgs/{orgId}/roles/{roleId}/widgets → reconcile the whole level in a
   single transaction. Returns the re-resolved effective list (explicit +
   inherited), same shape as the role-widgets GET. An empty array clears all
   explicit pins at this level (everything reverts to inherited). */
export async function saveRoleWidgets(
  orgId: string,
  roleId: string,
  widgets: WidgetPin[],
): Promise<RoleWidget[]> {
  const enabledWidgets = widgets.filter((w) => w.enabled);
  const res = await putAPI(`${roleWidgetsUrl(orgId, roleId)}/widgets`, { widgets: enabledWidgets });
  return (res?.data?.widgets ?? []) as RoleWidget[];
}

/* DELETE .../orgs/{orgId}/roles/{roleId} → reset the whole level to inherited.
   Clears ALL of the role's explicit rows at this org unit (grants AND
   override-offs), so the level falls back to pure inheritance from its
   ancestors. Returns the now-inherited effective list (empty if no ancestor has
   grants). Different from disableRoleWidget, which only clears one widget. */
export async function resetRoleWidgets(
  orgId: string,
  roleId: string,
): Promise<RoleWidget[]> {
  const res = await deleteAPI(roleWidgetsUrl(orgId, roleId));
  return (res?.data?.widgets ?? []) as RoleWidget[];
}

/* Persist a batch of personal (my-widgets) changes behind one Save click. There
   is no bulk my-widgets endpoint, so the per-widget PUT/DELETE calls are fired
   together; the final my-widgets state is returned. Only changed widgets should
   be passed in. */
export async function saveMyWidgets(
  orgUnitId: string,
  changes: WidgetPin[],
): Promise<MyWidgetsResponse> {
  if (changes.length === 0) return fetchMyWidgets();
  await Promise.all(changes.map((c) => toggleMyWidget(c.widget, orgUnitId, c.enabled)));
  /* Re-read so the UI renders from the authoritative server state. */
  return fetchMyWidgets();
}

/* ── Role-based dashboard header labels ────────────────────────────────────
   Title + subtitle reflect the logged-in user's role (roles come from the
   auth/permission API), e.g. "ISSO Dashboard" / "GRC Manager Dashboard".
   Shared by the group (GRCManagerDashboard) and org (GRCDashboardNew) dashboards
   so both the main page and the drill-down side panel read the same way. */
const ROLE_ACRONYMS = new Set(["ISSO", "GRC", "CISO", "SSP", "POAM", "IT", "AO", "RA"]);
const normRole = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");

/* Pretty primary-role label — acronyms stay , others Title-cased. */
function prettyRole(roles: string[]): string {
  const raw = roles[0]?.trim();
  if (!raw) return "";
  return raw
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      ROLE_ACRONYMS.has(w.toUpperCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

export function roleDashboardTitle(roles: string[]): string {
  /* GRC Manager shows simply as "GRC Dashboard" (the word "Manager" is hidden);
     the role is still resolved from the API. */
  if (normRole(roles[0] ?? "") === "grcmanager") return "GRC Dashboard";
  const r = prettyRole(roles);
  return r ? `${r} Dashboard` : "Dashboard";
}

/* Per-role one-line compliance description shown under the title. */
const ROLE_SUBTITLES: Record<string, string> = {
  systemadmin:   "Platform-wide governance, risk and compliance overview.",
  administrator: "Organization-wide governance, risk and compliance overview.",
  admin:         "Organization-wide governance, risk and compliance overview.",
  platformowner: "Platform-wide governance, risk and compliance overview.",
  ciso:          "Executive view of compliance posture and risk across the organization.",
  grcmanager:    "Group-level governance, risk and compliance overview.",
  isso:          "Security and compliance posture for the systems you oversee.",
  auditor:       "Compliance evidence and audit readiness across your scope.",
  assessor:      "Assessment progress and findings across your scope.",
  leadassessor:  "Assessment oversight and findings across your scope.",
  systemowner:   "Compliance and risk for the systems you own.",
};

export function roleDashboardSubtitle(roles: string[]): string {
  for (const r of roles) {
    const hit = ROLE_SUBTITLES[normRole(r)];
    if (hit) return hit;
  }
  return "Welcome back! Here's your compliance overview.";
}
